import { Failure, Ok } from "@megaron/result";
import { Serializer } from "@megaron/serializers";

export type ResourceId<T extends string = string> = `${T}:${string}`;

export const ResourceId = <TNamespace extends string = string>(type: TNamespace, id: string): ResourceId<TNamespace> =>
  `${type}:${id}`;

export const deconstructResourceId = <T extends string = string>(resourceId: ResourceId<T>) => {
  const [namespace, identifier] = resourceId.split(":") as [T, string];

  return { namespace, identifier };
};

export const resourceIdSerializer = <T extends string = string>(...types: T[]): Serializer<ResourceId<T>> => ({
  schema: { kind: "resourceId", types },
  serialize: (id) => id,
  deserialize: (raw: unknown) => {
    if (typeof raw !== "string") return Failure("NotAString");
    const [type, id] = raw.split(":") as [T, string];
    if (type === undefined || id === undefined) return Failure("InvalidResourceId");
    if (!types.includes(type)) return Failure("InvalidResourceType");

    return Ok(raw as ResourceId<T>);
  },
});

export type ExtractResourceId<T extends ResourceId, TNamespace extends string> =
  T extends ResourceId<TNamespace> ? T : never;

type ResourceIdNamespace<T> = T extends ResourceId<infer TNamespace> ? TNamespace : never;

export const matchResourceId =
  <T extends ResourceId>(mappers: {
    [namespace in ResourceIdNamespace<T>]: (id: string) => string;
  }) =>
  (resourceId: T): string => {
    const { identifier, namespace } = deconstructResourceId<ResourceIdNamespace<T>>(resourceId as any);
    return mappers[namespace](identifier);
  };

export const matchString =
  <T extends string>(map: {
    [namespace in T]: string;
  }) =>
  (str: string): string => {
    const r = map[str as T];
    if (!r) throw new Error(`No match for ${str}`);
    return r;
  };
